import {z} from 'zod'
import {isString, mapValues, omit, pick, pipe} from 'remeda'
import {
  zAmountType,
  zQuestionCfgShape,
  zQuestionForecastType,
  zResolutionCfg,
  zResolutionsStandalone,
  zSubqType
} from 'shared/forecast'
import {doTo, ok, type Err, type OK, type Res} from 'shared/promise'
import {deepEqual} from 'shared/collection'
import {notNullOrEmpty} from 'shared/collection'
import {match} from 'ts-pattern'
import {JsonObject, parseOrErr} from 'shared/type'
import {filterObj} from 'shared/collection'
import {zComparison} from 'shared/forecast/comparison'
import {uri} from 'shared/uri'
import {apiClient, okSchema, zDefaultError} from 'shared/apiClient'

export namespace AutoQ {
  const zBasicQ = z.object({
    title: z.string().optional(),
    subTitle: z.string().optional(),
    description: z.string().optional(),
    forecastType: zQuestionForecastType.optional(),
    amountType: zAmountType.optional(),
    unitLabel: z.string().optional()
  })
  const zRequestOptions = z.object({
    workspace_id: z.string(),
    verbose: z.boolean().optional(),
    forceRefresh: z.boolean().optional(),
    profile_id: z.string().optional()
  })

  export const zReqSubq = z
    .object({
      type: z.literal('subq'),
      options: zRequestOptions.extend({disableComparisons: z.boolean().optional()}),
      ...pipe(
        {
          ...zBasicQ.shape,
          parent: z
            .object({
              ...pick(zBasicQ.shape, ['title', 'subTitle', 'description']),
              forecastType: zQuestionForecastType.optional()
            })
            .optional(),
          subquestionType: zSubqType.optional()
        },
        s => ({question: z.object(s), priorRequest: z.object(s).optional()})
      )
    })
    .strip()
  type ReqSubq = z.infer<typeof zReqSubq>
  export const zReqQuestion = z
    .object({
      type: z.literal('question'),
      options: zRequestOptions,
      question: zBasicQ,
      priorRequest: zBasicQ.optional()
    })
    .strip()
  type ReqQuestion = z.infer<typeof zReqQuestion>
  export const questionResponseShape = doTo(
    zQuestionCfgShape,
    ({forecastType, amountType, unitLabel, defaultBounds, hardBounds, resolutions, comparisons}) => ({
      forecastType: forecastType.optional(),
      amountType: amountType.optional(),
      unitLabel: unitLabel.optional(),
      defaultBounds: defaultBounds.optional(),
      hardBounds: hardBounds.optional(),
      resolutions: resolutions.optional(),
      comparisons: comparisons.optional()
    })
  )
  export const zQuestionResponse = z.object({
    type: z.literal('question'),
    question: z.object(questionResponseShape).optional(),
    usePrior: z.boolean().optional()
  })
  export type QuestionResponse = z.infer<typeof zQuestionResponse>
  export const zSubqResponse = z.object({
    type: z.literal('subq'),
    question: z
      .object({
        ...omit(questionResponseShape, ['resolutions']),
        subquestionType: zSubqType.optional(),
        resolutions: zResolutionsStandalone.optional(),
        consequenceResolutions: zResolutionsStandalone.optional(),
        amount: zResolutionCfg.optional(),
        likelihood: zResolutionCfg.optional()
      })
      .optional(),
    usePrior: z.boolean().optional()
  })
  export type SubqResponse = z.infer<typeof zSubqResponse>

  export const zSuggestion = z.object({
    property: z.enum(['title', 'subTitle', 'description']),
    type: z.enum(['suggestion', 'comment', 'praise']).default('suggestion'),
    newValue: z.string().optional(),
    comment: z.string().optional()
  })
  export type Suggestion = z.infer<typeof zSuggestion>
  export const zTitleSuggestions = z.object({
    suggestions: z.array(zSuggestion).optional(),
    usePrior: z.boolean().optional()
  })
  export type TitleSuggestions = z.infer<typeof zTitleSuggestions>

  export const client = apiClient({
    md: {
      getAutoQ: {
        zReq: z.discriminatedUnion('type', [zReqSubq, zReqQuestion]),
        zResOk: z.discriminatedUnion('type', [zQuestionResponse, zSubqResponse])
      },
      getTitleSuggestions: {
        zReq: z
          .object({
            options: zRequestOptions,
            ...pipe(
              {
                ...zBasicQ.shape,
                parent: zBasicQ.optional()
              },
              s => ({question: z.object(s), priorRequest: z.object(s).optional()})
            )
          })
          .strip(),
        zResOk: zTitleSuggestions
      },
      getComparisons: {
        zReq: z
          .object({
            options: zRequestOptions,
            question: z.object({
              ...zBasicQ.shape,
              forecastType: zQuestionForecastType,
              amountType: zAmountType.optional(),
              unitLabel: z.string().optional()
            }),
            existingComparisons: z.array(zComparison).optional()
          })
          .strip(),
        zResOk: z.object({
          comparisons: z.array(zComparison).optional(),
          usePrior: z.boolean().optional()
        })
      }
    },
    url: uri('/api/auto-question')
  } as const)

  export type ReqAutoQ = ReqQuestion | ReqSubq
  export const getAutoQ = async <T extends ReqAutoQ>(
    data: T,
    requestInit?: RequestInit
  ): Promise<OK<T extends 'question' ? QuestionResponse : SubqResponse> | Err<{message: string}>> => {
    const res = await client.post('getAutoQ', data, requestInit)
    if (!res.ok) return res
    const parseRes = match(data.type)
      .with('question', () => parseOrErr(zQuestionResponse.strip(), res))
      .with('subq', () => parseOrErr(zSubqResponse.strip(), res))
      .exhaustive()
    if (!parseRes.ok) return parseRes
    return ok(parseRes.data) as any
  }
  export type GetAutoQRequest = Parameters<typeof getAutoQ>[0]
  export type GetAutoQResult = Awaited<ReturnType<typeof getAutoQ>>

  export const getTitleSuggestions = client.makePost('getTitleSuggestions')
  export type GetTitleSuggestionsRequest = Parameters<typeof getTitleSuggestions>[0]
  export type GetTitleSuggestionsResult = Awaited<ReturnType<typeof getTitleSuggestions>>

  export const getComparisons = client.makePost('getComparisons')
  export type GetComparisonsRequest = Parameters<typeof getComparisons>[0]
  export type GetComparisonsResult = Awaited<ReturnType<typeof getComparisons>>

  type StripOptions<T> = {
    keys?: Array<keyof T | string> | ReadonlyArray<keyof T | string>
  }

  export const cfgStrip = <T extends JsonObject>(o: T, options?: StripOptions<T>): T =>
    pipe(
      pick(o, (options?.keys ?? []) as string[]),
      o =>
        mapValues(o as JsonObject, v =>
          match(v)
            .when(isString, s => s?.trim())
            .otherwise(v => v)
        ),
      o => filterObj(o, (_, v) => notNullOrEmpty(v))
    ) as T

  export const cfgEqual = <T extends JsonObject>(a: T, b: JsonObject, options?: StripOptions<T>) => {
    const aStripped = cfgStrip(a, options)
    const bStripped = cfgStrip(b as T, options)
    return deepEqual(aStripped, bStripped)
  }
}
